import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FadeLoader } from 'react-spinners';
import '../styles/LoginForm.css';

interface LoginFormProps {
  onSubmit: (credentials: { email: string; password: string }) => void;
  isLoading: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    } else {
      // Scroll to first invalid field
      const firstErrorField = Object.keys(errors).find(
        key => errors[key as keyof typeof errors]
      );
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
          element.focus();
        }
      }
    }
  };

  return (
    <form className="modern-login-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="email" className="form-label" style={{fontSize:'14px '}}>
          Email Address
        </label>
        <div className={`input-wrapper ${errors.email ? 'error' : ''}`}>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            disabled={isLoading}
            autoComplete="email"
            aria-describedby={errors.email ? 'email-error' : undefined}
            style={{fontSize:'14px'}}
          />
         
        </div>
        {errors.email && (
            <p id="email-error" className="error-message" role="alert">
              {errors.email}
            </p>
          )}
      </div>

      <div className="form-group">
        <label htmlFor="password" className="form-label" style={{fontSize:'14px'}}>
          Password
        </label>
        <div className={`input-wrapper ${errors.password ? 'error' : ''}`}>
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your password"
            disabled={isLoading}
            autoComplete="current-password"
            aria-describedby={errors.password ? 'password-error' : undefined}
            style={{fontSize:'14px'}}
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={togglePasswordVisibility}
            disabled={isLoading}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {showPassword ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                <path d="M2 2l20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
              </svg>
            )}
          </button>
        </div>
        {errors.password && (
            <p id="password-error" className="error-message" role="alert">
              {errors.password}
            </p>
          )}
      </div>

      <button
        type="submit"
        className="btn-primary btn-full"
        style={{ marginTop: '1.5rem' }}
        disabled={isLoading}
        aria-live="polite"
      >
        {isLoading ? (
          <>
            <FadeLoader
              color="#ffffff"
              loading={true}
              height={12}
              width={4}
              radius={2}
              margin={1}
              speedMultiplier={0.8}
            />
            Signing in...
          </>
        ) : (
          'Log In'
        )}
      </button>

      <div className="forgot-password-link">
        <Link to="/forgot-password" className="forgot-password-text">
          Forgot your password?
        </Link>
      </div>
    </form>
  );
};
